import { apiConstants } from "@data/constants/apiConstants"
import { authConstants } from "@data/constants/authConstants"
import { parentModeConstants } from "@data/constants/parentModeConstants"
import { authService } from "@data/services/authService"
import { fastTrackService } from "@data/services/fastTrackService"

const initialState = {
    families: {}, // Store families by family ID
    userFamilyMap: {}, // Map user_id -> family_id for quick lookup
    isParentMode: false, // Track if user is in parent mode
    parentModeFamilyId: null, // Store the family ID for parent mode (-1 for individual accounts)
    parentModeStartTime: null, // Timestamp when parent mode was activated
}

export function family(state = initialState, action) {
    switch (action.type) {
        case authService.getFamilyTokens.name + apiConstants._SUCCESS:
        case authService.validateToken.name + apiConstants._SUCCESS:
        case authService.getSanctumToken.name + apiConstants._SUCCESS:
        case authService.verifyLoginCode.name + apiConstants._SUCCESS:
        case fastTrackService.verifyRecognition.name + apiConstants._SUCCESS:
        case authConstants.ACCEPT_LOGIN_LINK: {
            const familyData = action.data?.family
            const familyTokens = action.data?.family_tokens

            // A fresh login introduces a new authenticated identity. Any
            // persisted parentModeFamilyId from a previous session would
            // otherwise leak into the new login and cause API calls to fire
            // with a stale student_id on the first attempt at entering
            // "Espace parent". Reset the selection so the user re-picks via
            // FamilyAccountSelection (which always calls
            // setParentModeFamilyId before validateToken). Not for the email
            // link: it can be opened inside a live session, which keeps its
            // mode when the link is for the same account (LoginLinkScreen
            // logs out itself when it switches).
            const isFreshLogin = [
                authService.getSanctumToken,
                authService.verifyLoginCode,
                fastTrackService.verifyRecognition,
            ].some(
                (service) =>
                    action.type === service.name + apiConstants._SUCCESS
            )
            const resetParentSelection = isFreshLogin
                ? {
                      parentModeFamilyId: null,
                      isParentMode: false,
                      parentModeStartTime: null,
                  }
                : {}

            // A phone number can open several families at once, so a login
            // lists them in `families`, each with its members. Without that
            // list, the one `family` owns every account of family_tokens.
            const listed = Array.isArray(action.data?.families)
            const families = listed
                ? action.data.families
                : familyData
                  ? [familyData]
                  : []

            if (families.length === 0) {
                return { ...state, ...resetParentSelection }
            }

            const newUserMappings = {}
            families.forEach((familyItem) => {
                familyItem.members?.forEach((member) => {
                    newUserMappings[member.id] = familyItem.id
                })
            })
            if (!listed) {
                familyTokens?.forEach((familyToken) => {
                    if (familyToken.user?.id) {
                        newUserMappings[familyToken.user.id] = familyData.id
                    }
                })
            }

            return {
                ...state,
                ...resetParentSelection,
                families: {
                    ...state.families,
                    ...Object.fromEntries(families.map((f) => [f.id, f])),
                },
                userFamilyMap: {
                    ...state.userFamilyMap, // Keep existing family mappings
                    ...newUserMappings,
                },
            }
        }

        case parentModeConstants.SET_IS_PARENT_MODE:
            return {
                ...state,
                isParentMode: action.newState,
                // Set timestamp when entering parent mode, clear when exiting
                parentModeStartTime: action.newState
                    ? new Date().getTime()
                    : null,
            }

        case parentModeConstants.SET_PARENT_MODE_FAMILY_ID:
            return {
                ...state,
                parentModeFamilyId: action.familyId,
            }

        case authConstants.LOGOUT_401:
        case authConstants.RESET_AUTH_STATE:
        case authService.logout.name + apiConstants._SUCCESS:
        case authService.revokeSanctumToken.name + apiConstants._SUCCESS:
        case authService.deleteAccount.name + apiConstants._SUCCESS:
            return {
                ...state,
                isParentMode: false,
                parentModeFamilyId: null,
                parentModeStartTime: null,
            }

        case authService.bootstrapImpersonation.name + apiConstants._SUCCESS:
            // Drop the families/userFamilyMap caches when impersonation
            // starts. Otherwise a no-family impersonation target inherits
            // the previous session's family from the persisted store and
            // the chip picker shows unrelated children. isParentMode stays
            // as-is: the impersonation screen flips it true after bootstrap.
            return {
                ...state,
                families: {},
                userFamilyMap: {},
                parentModeFamilyId: null,
                parentModeStartTime: null,
            }

        case authService.exitImpersonation.name + apiConstants._SUCCESS:
            // Mirror the bootstrap reset AND drop isParentMode — otherwise
            // the AuthNavigator auto-redirect to family-account-selection
            // is gated off (it requires !isParentMode) and the admin lands
            // on the blank "redirecting" view forever.
            return {
                ...state,
                families: {},
                userFamilyMap: {},
                isParentMode: false,
                parentModeFamilyId: null,
                parentModeStartTime: null,
            }

        default:
            return state
    }
}
